import { usePageContent, useXcoreQuery } from "@robe/client";
import { Box, Button, Flex, Modal, Stack, Typography } from "@xcorejs/ui";
import SelectProductCaptcha from "components/Captcha/SelectProductCaptcha";
import { ErrorAnimatedIcon, SuccessAnimatedIcon } from "../Icons/AnimatedIcon";
import { Checkbox, FormControl, Input, Textarea } from "design-system/xcore/Form";
import { Field, FieldProps, Form, Formik } from "formik";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useUser } from "hooks/useUser";
import { General, Product, Products, TechnicalTrainingCalendar } from "xcore/types";
import { object, string } from "yup";
import { CaptchaStates } from "../Captcha/SelectProductCaptcha";
import Richtext from "../../design-system/Richtext";
import { useRouter } from "next/router";
import { WebUser } from "@robe/core";
import { useLayout } from "xcore";
import { LoadingPage } from "components/withAuth";

interface TechnicalTrainingFormModel {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  body: string;
}

interface TechnicalTrainingModalProps {
  trainingName: string;
  id: number;
  subject: string;
  body: string;
  inquiredInnovations: string[];
  setInquiredInnovations: Dispatch<SetStateAction<string[]>>;
  captchaItems: Product[];
}

export const TechnicalTrainingModal = ({
  trainingName,
  id,
  inquiredInnovations,
  setInquiredInnovations,
  subject,
  body,
  captchaItems,
}: TechnicalTrainingModalProps) => {
  return (
    <Modal
      header={<Flex flexDirection="column">{trainingName}</Flex>}
      _header={{ justifyContent: "center" }}
      size="lg"
      _overlay={{
        zIndex: 5,
      }}
    >
      <TechnicalTrainingForm
        id={`${id}`}
        inquiredInnovations={inquiredInnovations}
        setInquiredInnovations={setInquiredInnovations}
        subject={subject}
        body={body}
        captchaItems={captchaItems}
      />
    </Modal>
  );
};

interface TechnicalTrainingFormProps {
  id: string;
  inquiredInnovations: string[];
  setInquiredInnovations: Dispatch<SetStateAction<string[]>>;
  subject: string;
  body: string;
  captchaItems: Product[];
}

export const TechnicalTrainingForm = ({
  id,
  inquiredInnovations,
  setInquiredInnovations,
  subject,
  body,
  captchaItems,
}: TechnicalTrainingFormProps) => {
  const { locale } = useRouter();
  const { core, stringify } = useLayout();
  const { values } = usePageContent<TechnicalTrainingCalendar>();
  const { user } = useUser();
  const [submitResult, setSubmitResult] = useState<"success" | "error" | null>(null);
  const [checkbox, setCheckbox] = useState<boolean>(false);
  const [captcha, setCaptcha] = useState<CaptchaStates>(user ? "correct" : "null");

  // For some reason `general` cannot be passed to modal so it needs to be fetched here.
  // It is a small sacrifice for a greater good.
  const { data } = useXcoreQuery((c) =>
    c.content.listByTypes(["general", "products"], {
      fields: ["validEmail", "requiredField", "newIconText"],
      locales: [],
    })
  );

  const handleSubmit = ({ body: emailBody, ...formValues }: TechnicalTrainingFormModel) => {
    core.web
      .postTechnicalTraining({
        locale: locale ?? "en",
        technicalTrainingId: id,
        ...formValues,
        ...(emailBody.length && { body: emailBody }),
        recipient: formValues.email,
        subject: stringify(values?.confirmationSubject)!.length ? stringify(values?.confirmationSubject) : subject,
      })
      .then((res) => {
        if (res.ok) {
          setSubmitResult("success");
          setInquiredInnovations([...inquiredInnovations, id]);

          return;
        }

        setSubmitResult("error");
      })
      .catch(() => {
        setSubmitResult("error");
      });
  };

  const general = data?.contents[0] as General<"validEmail" | "requiredField">;
  const products = data?.contents[1] as Products<"newIconText">;

  const technicalTrainingSchema = useMemo(() => {
    if (!general) {
      return;
    }

    return object().shape({
      firstName: string().required(),
      lastName: string().required(),
      email: string().email(stringify(general.values.validEmail)).required(stringify(general.values.requiredField)),
      phoneNumber: string().required(stringify(general.values.requiredField)),
      body: string(),
    });
  }, [stringify, general]);

  if (!data?.contents) {
    return <LoadingPage />;
  }

  return submitResult ? (
    <Flex flexDirection="column" alignItems="center" mt="1rem">
      {submitResult === "success" ? <SuccessAnimatedIcon /> : <ErrorAnimatedIcon />}
      <Typography mt="3rem">{stringify(submitResult === "success" ? values.successMessage : values.errorMessage)}</Typography>
    </Flex>
  ) : (
    <Formik initialValues={initialValues(user, body)} validationSchema={technicalTrainingSchema} onSubmit={handleSubmit}>
      {({ isValid }) => (
        <Form>
          <Box mt="3rem">
            <Stack direction="column" gap="2rem">
              <Stack direction={{ _: "column", sm: "row" }}>
                <Field name="firstName">
                  {({ field }: FieldProps) => (
                    <FormControl labelText={stringify(values.firstName)} context="isRequired" width="100%" pr="2.5rem">
                      <Input inputSize="md" width="100%" required {...field} />
                    </FormControl>
                  )}
                </Field>

                <Field name="lastName">
                  {({ field }: FieldProps) => (
                    <FormControl labelText={stringify(values.lastName)} context="isRequired" width="100%">
                      <Input inputSize="md" width="100%" required {...field} />
                    </FormControl>
                  )}
                </Field>
              </Stack>

              <Stack direction={{ _: "column", sm: "row" }}>
                <Field name="email">
                  {({ field, meta }: FieldProps) => (
                    <FormControl
                      labelText={stringify(values.mail)}
                      context="isRequired"
                      errorContext={meta.error && meta.touched ? "isInvalid" : "normal"}
                      {...(meta.touched && { error: meta.error })}
                      width="100%"
                      pr="2.5rem"
                    >
                      <Input inputSize="md" width="100%" type="email" required {...field} />
                    </FormControl>
                  )}
                </Field>

                <Field name="phoneNumber">
                  {({ field, meta }: FieldProps) => (
                    <FormControl
                      labelText={stringify(values.phoneNumber)}
                      context="isRequired"
                      errorContext={meta.error && meta.touched ? "isInvalid" : "normal"}
                      {...(meta.touched && { error: meta.error })}
                      width="100%"
                    >
                      <Input inputSize="md" width="100%" required {...field} />
                    </FormControl>
                  )}
                </Field>
              </Stack>

              <Stack direction={{ _: "column", sm: "row" }}>
                <FormControl labelText={stringify(values.modalSubjectLabel)} context="isReadOnly" width="100%">
                  <Input inputSize="md" width="100%" placeholder={subject} disabled />
                </FormControl>
              </Stack>

              <Field name="body">
                {({ field }: FieldProps) => (
                  <FormControl labelText={stringify(values.modalDescLabel)} width="100%" context="isRequired">
                    <Typography color="grey" fontSize="1.3rem">
                      {stringify(values.senderInfoInEmail)}
                    </Typography>
                    <Textarea inputSize="md" width="100%" minHeight={{ _: "16rem", md: "15rem" }} {...field}>
                      {body}
                    </Textarea>
                  </FormControl>
                )}
              </Field>

              {user ? (
                <>
                  <Box position="relative" zIndex={10}>
                    <Checkbox mt="3rem" checked={checkbox} onChange={() => setCheckbox((v) => !v)}>
                      <Richtext value={values.gdprConsent} />
                    </Checkbox>
                  </Box>

                  <Flex flexDirection={{ _: "column", sm: "row" }} justifyContent="space-between" alignItems="center" mt="3rem">
                    <Button type="submit" disabled={!isValid || !checkbox || captcha !== "correct"}>
                      {stringify(values.modalSendBtn)}
                    </Button>
                  </Flex>
                </>
              ) : (
                <SelectProductCaptcha
                  items={captchaItems}
                  setCaptcha={setCaptcha}
                  newIconText={products?.values?.newIconText}
                  mt="2rem"
                  _selectProps={{ isSelectInModal: true }}
                >
                  <Box position="relative" zIndex={10}>
                    <Checkbox mt="3rem" checked={checkbox} onChange={() => setCheckbox((v) => !v)}>
                      <Richtext value={values.gdprConsent} />
                    </Checkbox>
                  </Box>

                  <Flex flexDirection={{ _: "column", sm: "row" }} justifyContent="space-between" alignItems="center" mt="3rem">
                    <Button type="submit" disabled={!isValid || !checkbox || captcha !== "correct"}>
                      {stringify(values.modalSendBtn)}
                    </Button>
                  </Flex>
                </SelectProductCaptcha>
              )}
            </Stack>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

const initialValues = (user: WebUser | null | undefined, body: string) => ({
  firstName: user?.firstName ?? "",
  lastName: user?.lastName ?? "",
  email: user?.email ?? "",
  phoneNumber: user?.phone ?? "",
  body: body ?? "",
});
